import { Component, Inject, Input, OnInit, Optional } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { finalize } from "rxjs/operators";
import { forkJoin } from "rxjs";
import { ToastService } from "src/app/core";
import { ConfigurationApi, DocumentConfigs, DocumentTypes, LookupField, OfficeEntity } from "src/app/core/api.gen";
import { LoaderService } from "src/app/core/loader.service";


@Component({
  selector: "eos-governance-doc-types-edit",
  templateUrl: "./governance-doc-types-edit.component.html",
})
export class GovernanceDocTypesEditComponent implements OnInit {
  @Input() activity: DocumentConfigs;
  @Input() countries: LookupField[] = [];
  @Input() officeLocations: LookupField[] = [];
  @Input() officeLegalEntities: OfficeEntity[] = [];
  @Input() documentTypes: DocumentTypes[] = [];
  @Input() isEdit: boolean = false;
  @Input() countryId: number = 0;
  @Input() existingConfigs: DocumentConfigs[] = [];

  officeLocationsInfo: LookupField[] = [];
  officeLegalEntitiesInfo: OfficeEntity[] = [];

  get isEditMode(): boolean {
    return this.activity !== undefined && this.isEdit;
  }

  createForm = new UntypedFormGroup({
    id: new UntypedFormControl(0),
    country: new UntypedFormControl(null, Validators.required),
    location: new UntypedFormControl(null, Validators.required),
    legalEntity: new UntypedFormControl(null, Validators.required),
    documentType: new UntypedFormControl(null, Validators.required),
    isActive: new UntypedFormControl(true),
  });


  constructor(
    public modal: NgbActiveModal,
    private loader: LoaderService,
    private toast: ToastService,
    private configApi: ConfigurationApi,
  ) {}

    ngOnInit() {
      const selectedId = this.isEditMode ? this.activity?.documentTypeId : null;
      this.documentTypes = this.documentTypes.filter(t => t.isActive || t.id === selectedId);

      if (this.countryId !== 0) {
        this.createForm.controls.country.setValue(this.countryId);
        this.filterByCountry(this.countryId);
      }

      if (this.isEditMode) {
        this.bindActivityInfo(this.activity);
        this.createForm.controls.country.disable();
        this.createForm.controls.location.disable();
        this.createForm.controls.legalEntity.disable();
        this.createForm.controls.documentType.disable();
      }
    }

  bindActivityInfo(r: DocumentConfigs) {
    if (r.countryId) {
      this.filterByCountry(r.countryId);
    }
    this.createForm.patchValue({
      id: r.id,
      country: r.countryId ?? null,
      location: r.locationId,
      legalEntity: r.legalEntityId ?? null,
      documentType: r.documentTypeId,
      isActive: r.isActive ?? true,
    });
  }

  onCountryChange($event) {
    this.createForm.controls.location.reset();
    this.createForm.controls.legalEntity.reset();
    if ($event) {
      this.filterByCountry($event.id);
    } else {
      this.officeLocationsInfo = [];
      this.officeLegalEntitiesInfo = [];
    }
  }

  filterByCountry(countryId: number) {
    this.officeLocationsInfo = this.officeLocations.filter(i => i.parentFieldId === countryId);
    this.officeLegalEntitiesInfo = this.officeLegalEntities.filter(i => i.parentFieldId === countryId);
  }

  saveActivity() {
    if (this.createForm.invalid || this.isDuplicate) return;

    const raw = this.createForm.getRawValue();
    const locations: number[] = Array.isArray(raw.location) ? raw.location : [raw.location];
    const legalEntities: (number | null)[] = Array.isArray(raw.legalEntity) && raw.legalEntity.length > 0
      ? raw.legalEntity
      : [raw.legalEntity ?? null];

    const payloads = locations.flatMap(loc =>
    legalEntities.map(le => DocumentConfigs.fromJS({
      id: raw.id ?? 0,
      countryId: raw.country ?? undefined,
      locationId: loc,
      legalEntityId: le ?? undefined,
      documentTypeId: raw.documentType,
      isActive: raw.isActive,
    }))
  );

    this.loader.show();
    forkJoin(payloads.map(p => this.configApi.saveDocumentConfiguration(p)))
      .pipe(finalize(() => this.loader.clear()))
      .subscribe(
        () => { this.toast.success("Successfully created document configuration"); this.modal.close("success"); },
        () => this.toast.error("Error occurred, please try again!")
      );
  }

  get isDuplicate(): boolean {
    if (this.isEditMode) return false;

    const raw = this.createForm.getRawValue();
    const docTypeId = raw.documentType;
    if (!docTypeId) return false;

    const selectedLocations: number[] = Array.isArray(raw.location) && raw.location.length > 0
      ? raw.location
      : [raw.location];

    const selectedLEs: (number | null)[] = Array.isArray(raw.legalEntity) && raw.legalEntity.length > 0
      ? raw.legalEntity
      : [raw.legalEntity ?? null];

    return selectedLocations.some(loc =>
      selectedLEs.some(le =>
        this.existingConfigs.some(c =>
          c.documentTypeId === docTypeId &&
          c.locationId === loc &&
          (c.legalEntityId ?? null) === (le ?? null)
        )
      )
    );
  }


  onCancel() {
    this.modal.dismiss();
  }
}